import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, TrendingUp, FileText, Clock, Repeat } from 'lucide-react';

const DigitalisierungProzessoptimierung = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Digitalisierung & Prozessoptimierung Mönchengladbach',
    description: 'Arbeitsabläufe digitalisieren und Prozesse vereinfachen für KMU in Mönchengladbach. Pragmatisch und ohne unnötige Komplexität.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'Digitalisierung'
  };

  const probleme = [
    'Viele manuelle Arbeitsschritte, die Zeit fressen',
    'Informationen liegen verstreut: E-Mails, Zettel, Excel, verschiedene Ordner',
    'Unklare Abläufe – jeder macht es anders',
    'Fehler durch Vergessen oder Übersehen',
    'Suchen kostet mehr Zeit als arbeiten'
  ];

  const ansatz = [
    {
      icon: Target,
      title: 'Schritt für Schritt',
      text: 'Nicht alles auf einmal. Wir starten dort, wo es sich wirklich lohnt.'
    },
    {
      icon: Clock,
      title: 'Zeitersparnis im Fokus',
      text: 'Digitalisierung muss sich rechnen. Ich schaue, wo Sie wirklich Zeit sparen können.'
    },
    {
      icon: Repeat,
      title: 'Einfache Lösungen',
      text: 'Keine überkomplexen Systeme. Was Sie nicht verstehen, werden Sie nicht nutzen.'
    }
  ];

  const beispiele = [
    {
      title: 'Angebotserstellung',
      vorher: 'Word-Vorlage suchen, Daten aus alter E-Mail kopieren, Preise nachschlagen, PDF erstellen, per E-Mail verschicken',
      nachher: 'Formular ausfüllen, Knopf drücken. Angebot wird automatisch erstellt und versendet.'
    },
    {
      title: 'Urlaubsverwaltung',
      vorher: 'Zettel ausfüllen, Chef unterschreiben lassen, im Ordner ablegen, manuell in Excel eintragen',
      nachher: 'Online-Formular, automatische Freigabe, Kalender wird direkt aktualisiert.'
    },
    {
      title: 'Rechnungsfreigabe',
      vorher: 'Rechnung kommt per Post, wird kopiert, weitergereicht, unterschrieben, abgeheftet, in Buchhaltung gegeben',
      nachher: 'PDF wird automatisch erkannt, zugeordnet, digital freigegeben, an Steuerberater übermittelt.'
    }
  ];

  const vorgehen = [
    'Ist-Aufnahme: Wo hakt es aktuell?',
    'Potenzial erkennen: Welche Abläufe lohnen sich zu digitalisieren?',
    'Passende Tools finden: Was Sie schon haben (z.B. Microsoft 365) oder einfache Ergänzungen',
    'Prozess einrichten: Ich baue es auf, Sie testen es',
    'Schulung: Ihr Team lernt, wie es funktioniert',
    'Anpassung: Wir verbessern, wo es nötig ist'
  ];

  const relatedPages = [
    { title: 'Microsoft 365 Optimierung', url: '/microsoft-365-optimierung', description: 'Tools, die Sie schon haben, besser nutzen' },
    { title: 'IT-Infrastruktur', url: '/it-infrastruktur-kmu', description: 'Die Basis für digitale Prozesse' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Digitalisierung & Prozessoptimierung für KMU in Mönchengladbach – Schteffann"
        description="Arbeitsabläufe digitalisieren und Prozesse vereinfachen für KMU in Mönchengladbach, Viersen und Krefeld. Pragmatische Digitalisierung ohne unnötige Komplexität."
        keywords="Digitalisierung Mönchengladbach, Prozessoptimierung KMU, Arbeitsabläufe digitalisieren, Digitalisierung Handwerk, Microsoft 365 Prozesse, Mönchengladbach, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <TrendingUp className="w-4 h-4 mr-2" />
              Digitalisierung & Prozesse
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Digitalisierung für KMU in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Digitalisierung heißt nicht, alles umzukrempeln. Ich helfe KMU in Mönchengladbach, Viersen und Krefeld, Schritt für Schritt Zeit zu sparen – dort, wo es sich wirklich lohnt.
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Typische Zeitfresser in KMU</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-700 mt-8 font-medium">
                  Jeder dieser Punkte kostet Sie Geld. Und lässt sich verbessern.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ansatz */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Mein Ansatz
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Digitalisierung muss nicht kompliziert sein. Und nicht alles auf einmal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ansatz.map((item, index) => (
              <Card key={index} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl mb-6">
                    <item.icon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beispiele */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Vorher / Nachher – Beispiele
            </h2>
            <p className="text-xl text-slate-600">
              So sieht Digitalisierung in der Praxis aus:
            </p>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {beispiele.map((beispiel, index) => (
              <Card key={index} className="border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">{beispiel.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">Vorher</h4>
                      <p className="text-slate-700">{beispiel.vorher}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">Nachher</h4>
                      <p className="text-slate-700">{beispiel.nachher}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Wie ich vorgehe</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {vorgehen.map((schritt, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center text-cyan-700 font-bold">
                        {index + 1}
                      </div>
                      <p className="text-lg text-slate-700 pt-1">{schritt}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Das könnte Sie auch interessieren</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {relatedPages.map((page, index) => (
              <Link key={index} to={page.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                      {page.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{page.description}</p>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Sparen Sie Zeit – Schritt für Schritt
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns schauen, wo Digitalisierung bei Ihnen am meisten bringt.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DigitalisierungProzessoptimierung;
